// MUI
import { Box, Grid, Typography } from "@mui/material";

// Components
import MultipleSelectGroup from "@Layouts/components/MultipleSelectGroup/MultipleSelectGroup.jsx";
import CustomizedSwitches from "@Layouts/components/InputComponents/CustomizedSwitches";

// Icons
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";

// Types
import { IBasicMappedPosition } from "@Common/types/positions/api";
import { LabelObject } from "@Settings/types/PositionMapping/model";

type PositionMappingSectionRowProps = {
	title: string;
	item: IBasicMappedPosition;
	switchablePositions: string[];
	allowDeleteFirst?: boolean;
	isMutating: boolean;
	showErrors?: boolean;
	isDropdownRequired: boolean;
	selectablePositions: LabelObject[];
	isExpanded: boolean;
	disableClearable?: boolean;
	hasEditPermission: boolean;
	toggleRowExpansion: (position: string) => void;
	iconStyle: (position: string) => { transform: string };
	getEmptyCells: (positions: string[] | undefined) => boolean[];
	onChangePositionCategory: (position: string) => void;
	// Payroll Position Operations
	onChangePayrollPosition: (value: string, systemPosition: string, order: number) => void;
	onDeletePayrollPosition: (index: number, systemPosition: string) => void;
	onAddPayrollPosition: (position: string) => void;
};

/**
 * Position Mapping Section Row - This component is responsible for rendering each of the Position Mapping Section Row.
 */
export default function PositionMappingSectionRow(props: Readonly<PositionMappingSectionRowProps>) {
	const {
		item,
		isExpanded,
		hasEditPermission,
		isMutating,
		iconStyle,
		onChangePositionCategory,
		getEmptyCells,
		showErrors = true,
	} = props;

	return (
		<Grid container alignItems="center">
			<Grid item xs={2.5}>
				<Typography variant={"subtitle1"} className="position-mapping-table-content">
					{props.switchablePositions.includes(item.systemPosition) ? (
						<>
							<Box
								style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
								onClick={() => props.toggleRowExpansion(item.systemPosition)}
							>
								<span>{item.systemPosition}</span>
								<ArrowForwardIosRoundedIcon className="arrow-icon" style={iconStyle(item.systemPosition)} />
							</Box>

							<CustomizedSwitches
								hidden={!isExpanded}
								disabled={!hasEditPermission || isMutating}
								textRight
								textChecked="Departmental"
								textUnChecked="Producer"
								onChange={() => onChangePositionCategory(item.systemPosition)}
								checked={item.isDepartmental}
							/>
						</>
					) : (
						item.systemPosition
					)}
				</Typography>
			</Grid>
			<Grid item xs={9.5}>
				<MultipleSelectGroup
					// Data
					identifier={item.systemPosition}
					items={item.payrollPosition}
					selectableOptions={props.selectablePositions}
					disabled={!props.hasEditPermission || props.isMutating}
					disableAddButton={!props.hasEditPermission || props.isMutating}
					errorsList={showErrors ? getEmptyCells(item.payrollPosition) : []}
					// Options
					allowDeleteFirst
					disableClearable
					showDeleteIcon
					showErrorMessage={false}
					// Styles
					placeholder={"Select an Option"}
					inputProps={{ width: 215 }}
					// Callbacks
					onChange={(selected: { label: string }, order: number) =>
						props.onChangePayrollPosition(selected?.label ?? "", item.systemPosition, order)
					}
					onDelete={(order: number) => props.onDeletePayrollPosition(order, item.systemPosition)}
					onAdd={() => props.onAddPayrollPosition(item.systemPosition)}
				/>
			</Grid>
		</Grid>
	);
}
